import { Component, inject } from '@angular/core';
import { OrganisationService } from '../../libs/services/organisation.service';

/**
 * The shell's standing reminder that the organisation on screen is not the viewer's own
 * (plan §5, stage C1).
 *
 * It sits in the app root rather than on any one page because the state it reports is a
 * property of the session, not of a route: an admin who picked a customer organisation on
 * the dashboard is still inside it in the builder, in App Studio and on every settings
 * page, and every request they make from any of them carries that organisation. A banner
 * that only appeared on the page where the choice was made would be worse than none.
 *
 * Renders nothing at all for a customer user, and nothing for an admin working in one of
 * their own organisations: `actingAsAdministrator` answers false until the token has been
 * decoded, so there is no boot flash.
 */
@Component({
  standalone: true,
  selector: 'finxone-workflow-frontend-acting-org-banner',
  templateUrl: './acting-org-banner.component.html',
  styleUrls: ['./acting-org-banner.component.scss'],
})
export class ActingOrgBannerComponent {
  public readonly organisationService = inject(OrganisationService);

  /** True only when there is an organisation of the viewer's own to go back to. */
  public get canReturnHome(): boolean {
    return !!this.organisationService.homeOrgId();
  }

  /**
   * Leaves the customer organisation. Reloads for the same reason switching in does: the
   * org header changes, so everything fetched under the customer's organisation is stale.
   */
  public returnHome(): void {
    const home = this.organisationService.homeOrgId();
    if (!home) {
      return;
    }
    const name = this.organisationService.memberOrgs().find((org) => org.id === home)?.name;
    this.organisationService.setActiveOrg(home, name);
    location.reload();
  }
}
